import {
  createContextWithViewHeaderDescription,
  useApiClient,
  useHomeSitemap,
  useNavigation,
  useSitemap,
  useSitemapItems,
  useTranslation,
} from '@msdk/common'
import { CustomItem, Sitemap, SitemapItem, SitemapReferenceItem, ViewItem } from '@msdk/definitions'
import { NoAccessIcon, styled, THEME_CONST } from '@msdk/ui'
import { useEffect, useState } from 'react'
import { findSitemapItem } from 'utils'

const Wrapper = styled('div')`
  height: calc(100vh - ${THEME_CONST.navHeight}px - 58px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const MessageContainer = styled('div')`
  max-width: 75vh;
  display: flex;
  flex-direction: column;
  align-items: center;
`

const MessageTitle = styled('p')`
  font-weight: 700;
  font-size: 24px;
  line-height: 32px;
  text-align: center;
  margin: 48px 0 16px;
`

const MessageSubtitle = styled('p')`
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  margin: 0;
  text-align: center;
  color: #0009;
`

export function HomeSelector() {
  const navigate = useNavigation()
  const apiClient = useApiClient()
  const getHomeSitemap = useHomeSitemap()
  const [sitemapModel, setSitemapModel] = useState<Sitemap>()
  const defaultSitemap = useSitemap('DefaultArea')
  const sitemapItems = useSitemapItems()

  const [noItemsAvailable, setNoItemsAvailable] = useState(false)
  const t = useTranslation()

  useEffect(() => {
    const fetch = async () => {
      const sitemapName = await getHomeSitemap()
      const sitemap = await apiClient.metadata.getSiteMap(sitemapName)
      setSitemapModel(sitemap)
    }
    fetch().catch(console.error)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (sitemapModel) {
      let item: SitemapItem | undefined = undefined
      /**
       * Check if the home sitemap has any areas defined, and then get the first item that can be shown (ViewItem, CustomItem)
       * If the home sitemap does not contain any area we fallback to the default sitemap and get the first showable item in there
       * NOTE: the home sitemap could be empty in case there aren't any MS enabled for that tenant that are enabled.
       */
      if (sitemapModel.areas.length && findSitemapItem(sitemapModel) !== undefined) {
        item = findSitemapItem(sitemapModel)
      } else if (defaultSitemap.areas.length && findSitemapItem(defaultSitemap) !== undefined) {
        item = findSitemapItem(defaultSitemap)
      }

      if (item) {
        switch (item?.type) {
          case 'ViewItem':
            // eslint-disable-next-line no-case-declarations
            const viewItem = item as ViewItem
            if (!viewItem.entity) {
              const relatedItem = sitemapItems.find((i) => i.id === viewItem.id) as SitemapReferenceItem
              // eslint-disable-next-line no-case-declarations
              const ctx = createContextWithViewHeaderDescription(relatedItem.viewHeaderDescription)

              navigate(`${relatedItem.id}`, sitemapModel.name, ctx)
            } else {
              // eslint-disable-next-line no-case-declarations
              const ctx = createContextWithViewHeaderDescription(viewItem.viewHeaderDescription)

              navigate(`${viewItem.entity}/${viewItem.viewName}`, sitemapModel.name, ctx)
            }

            break
          case 'CustomItem':
            // eslint-disable-next-line no-case-declarations
            const customItem = item as CustomItem
            navigate(`CUSTOM/${customItem.name}`, sitemapModel.name)
            break
          default:
            throw new Error(`[Home] unsupported item type: ${item?.type}`)
        }
      } else {
        setNoItemsAvailable(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sitemapModel])

  return (
    <>
      {noItemsAvailable && (
        <Wrapper>
          <NoAccessIcon />
          <MessageContainer>
            <MessageTitle>{t('CMPSR_USER_NO_SITEMAP_ITEMS_TITLE', 'No access')}</MessageTitle>
            <MessageSubtitle>
              {t(
                'CMPSR_USER_NO_SITEMAP_ITEMS_SUBTITLE1',
                'It looks like you need permissions to view this page or perform this action.',
              )}
            </MessageSubtitle>
            <MessageSubtitle>
              {t('CMPSR_USER_NO_SITEMAP_ITEMS_SUBTITLE2', 'Contact your administrator for more information.')}
            </MessageSubtitle>
          </MessageContainer>
        </Wrapper>
      )}
    </>
  )
}
